/**
 * Colour parsing and formatting for the colour panel. The stored strings must stay byte-identical to what Spectrum
 * wrote with its TinyColor 1.1.2, so the parser, the HSV conversions and their rounding follow TinyColor's code,
 * with two differences: alpha keeps 3 decimals (Spectrum's change to TinyColor), and 8-digit hex is CSS
 * `#rrggbbaa`, read and written, where TinyColor used ARGB.
 */

/** A colour as TinyColor holds it: r, g, b unrounded in [0, 255], a in [0, 1]. */
export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** The panel's own state: hue, saturation and value as ratios in [0, 1], and alpha. */
export interface Hsva {
  h: number;
  s: number;
  v: number;
  a: number;
}

export interface ParsedColor extends Rgba {
  /** False for text no format matches; the colour is then black, as TinyColor made it. */
  ok: boolean;
}

const NAMES: Record<string, string> = Object.fromEntries((
  'aliceblue:f0f8ff antiquewhite:faebd7 aqua:0ff aquamarine:7fffd4 azure:f0ffff beige:f5f5dc bisque:ffe4c4 ' +
  'black:000 blanchedalmond:ffebcd blue:00f blueviolet:8a2be2 brown:a52a2a burlywood:deb887 burntsienna:ea7e5d ' +
  'cadetblue:5f9ea0 chartreuse:7fff00 chocolate:d2691e coral:ff7f50 cornflowerblue:6495ed cornsilk:fff8dc ' +
  'crimson:dc143c cyan:0ff darkblue:00008b darkcyan:008b8b darkgoldenrod:b8860b darkgray:a9a9a9 darkgreen:006400 ' +
  'darkgrey:a9a9a9 darkkhaki:bdb76b darkmagenta:8b008b darkolivegreen:556b2f darkorange:ff8c00 darkorchid:9932cc ' +
  'darkred:8b0000 darksalmon:e9967a darkseagreen:8fbc8f darkslateblue:483d8b darkslategray:2f4f4f ' +
  'darkslategrey:2f4f4f darkturquoise:00ced1 darkviolet:9400d3 deeppink:ff1493 deepskyblue:00bfff dimgray:696969 ' +
  'dimgrey:696969 dodgerblue:1e90ff firebrick:b22222 floralwhite:fffaf0 forestgreen:228b22 fuchsia:f0f ' +
  'gainsboro:dcdcdc ghostwhite:f8f8ff gold:ffd700 goldenrod:daa520 gray:808080 green:008000 greenyellow:adff2f ' +
  'grey:808080 honeydew:f0fff0 hotpink:ff69b4 indianred:cd5c5c indigo:4b0082 ivory:fffff0 khaki:f0e68c ' +
  'lavender:e6e6fa lavenderblush:fff0f5 lawngreen:7cfc00 lemonchiffon:fffacd lightblue:add8e6 lightcoral:f08080 ' +
  'lightcyan:e0ffff lightgoldenrodyellow:fafad2 lightgray:d3d3d3 lightgreen:90ee90 lightgrey:d3d3d3 ' +
  'lightpink:ffb6c1 lightsalmon:ffa07a lightseagreen:20b2aa lightskyblue:87cefa lightslategray:789 ' +
  'lightslategrey:789 lightsteelblue:b0c4de lightyellow:ffffe0 lime:0f0 limegreen:32cd32 linen:faf0e6 magenta:f0f ' +
  'maroon:800000 mediumaquamarine:66cdaa mediumblue:0000cd mediumorchid:ba55d3 mediumpurple:9370db ' +
  'mediumseagreen:3cb371 mediumslateblue:7b68ee mediumspringgreen:00fa9a mediumturquoise:48d1cc ' +
  'mediumvioletred:c71585 midnightblue:191970 mintcream:f5fffa mistyrose:ffe4e1 moccasin:ffe4b5 navajowhite:ffdead ' +
  'navy:000080 oldlace:fdf5e6 olive:808000 olivedrab:6b8e23 orange:ffa500 orangered:ff4500 orchid:da70d6 ' +
  'palegoldenrod:eee8aa palegreen:98fb98 paleturquoise:afeeee palevioletred:db7093 papayawhip:ffefd5 ' +
  'peachpuff:ffdab9 peru:cd853f pink:ffc0cb plum:dda0dd powderblue:b0e0e6 purple:800080 rebeccapurple:663399 ' +
  'red:f00 rosybrown:bc8f8f royalblue:4169e1 saddlebrown:8b4513 salmon:fa8072 sandybrown:f4a460 seagreen:2e8b57 ' +
  'seashell:fff5ee sienna:a0522d silver:c0c0c0 skyblue:87ceeb slateblue:6a5acd slategray:708090 slategrey:708090 ' +
  'snow:fffafa springgreen:00ff7f steelblue:4682b4 tan:d2b48c teal:008080 thistle:d8bfd8 tomato:ff6347 ' +
  'turquoise:40e0d0 violet:ee82ee wheat:f5deb3 white:fff whitesmoke:f5f5f5 yellow:ff0 yellowgreen:9acd32'
).split(' ').map((pair) => pair.split(':')));

// Parentheses and commas are optional; whitespace can take their place. Numbers may carry a percent sign.
const UNIT = '(?:[-\\+]?\\d*\\.\\d+%?)|(?:[-\\+]?\\d+%?)';
const MATCH3 = `[\\s|\\(]+(${UNIT})[,|\\s]+(${UNIT})[,|\\s]+(${UNIT})\\s*\\)?`;
const MATCH4 = `[\\s|\\(]+(${UNIT})[,|\\s]+(${UNIT})[,|\\s]+(${UNIT})[,|\\s]+(${UNIT})\\s*\\)?`;
const MATCHERS = {
  rgb: new RegExp('rgb' + MATCH3),
  rgba: new RegExp('rgba' + MATCH4),
  hsl: new RegExp('hsl' + MATCH3),
  hsla: new RegExp('hsla' + MATCH4),
  hsv: new RegExp('hsv' + MATCH3),
  hsva: new RegExp('hsva' + MATCH4),
  hex3: /^([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/,
  hex6: /^([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
  hex8: /^([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
};

type Channel = number | string;
interface ColorObject {
  r?: Channel; g?: Channel; b?: Channel;
  h?: Channel; s?: Channel; v?: Channel; l?: Channel;
  a?: Channel;
}

function isOnePointZero(n: Channel): boolean {
  return typeof n === 'string' && n.indexOf('.') !== -1 && parseFloat(n) === 1;
}

function isPercentage(n: Channel): boolean {
  return typeof n === 'string' && n.indexOf('%') !== -1;
}

/** Takes [0, max] or a percentage and returns [0, 1]; a percentage keeps 2 decimals of its value, as TinyColor. */
function bound01(value: Channel, max: number): number {
  const channel = isOnePointZero(value) ? '100%' : value;
  let n = Math.min(max, Math.max(0, parseFloat(String(channel))));
  if (isPercentage(channel)) {
    n = parseInt(String(n * max), 10) / 100;
  }
  if (Math.abs(n - max) < 0.000001) {
    return 1;
  }
  return (n % max) / max;
}

function boundAlpha(value: Channel): number {
  const a = parseFloat(String(value));
  return isNaN(a) || a < 0 || a > 1 ? 1 : a;
}

function convertToPercentage(n: Channel): Channel {
  return (n as number) <= 1 ? `${(n as number) * 100}%` : n;
}

function hsvToRgb(h: Channel, s: Channel, v: Channel): {r: number; g: number; b: number} {
  const hue = bound01(h, 360) * 6;
  const sat = bound01(s, 100);
  const val = bound01(v, 100);
  const i = Math.floor(hue);
  const f = hue - i;
  const p = val * (1 - sat);
  const q = val * (1 - f * sat);
  const t = val * (1 - (1 - f) * sat);
  const mod = i % 6;
  return {r: [val, q, p, p, t, val][mod] * 255, g: [t, val, val, q, p, p][mod] * 255, b: [p, p, t, val, val, q][mod] * 255};
}

function hslToRgb(h: Channel, s: Channel, l: Channel): {r: number; g: number; b: number} {
  const hue = bound01(h, 360);
  const sat = bound01(s, 100);
  const light = bound01(l, 100);
  if (sat === 0) {
    return {r: light * 255, g: light * 255, b: light * 255};
  }
  const hue2rgb = (low: number, high: number, t: number) => {
    if (t < 0) {
      t += 1;
    }
    if (t > 1) {
      t -= 1;
    }
    if (t < 1 / 6) {
      return low + (high - low) * 6 * t;
    }
    if (t < 1 / 2) {
      return high;
    }
    if (t < 2 / 3) {
      return low + (high - low) * (2 / 3 - t) * 6;
    }
    return low;
  };
  const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat;
  const p = 2 * light - q;
  return {r: hue2rgb(p, q, hue + 1 / 3) * 255, g: hue2rgb(p, q, hue) * 255, b: hue2rgb(p, q, hue - 1 / 3) * 255};
}

function stringToObject(text: string): ColorObject | null {
  // Leading spaces, commas and `#` go, so `#f00`, `f00` and ` ,#red` all read.
  let color = text.replace(/^[\s,#]+/, '').replace(/\s+$/, '').toLowerCase();
  if (Object.hasOwn(NAMES, color)) {
    color = NAMES[color];
  } else if (color === 'transparent') {
    return {r: 0, g: 0, b: 0, a: 0};
  }
  let match: RegExpExecArray | null;
  if ((match = MATCHERS.rgb.exec(color))) {
    return {r: match[1], g: match[2], b: match[3]};
  }
  if ((match = MATCHERS.rgba.exec(color))) {
    return {r: match[1], g: match[2], b: match[3], a: match[4]};
  }
  if ((match = MATCHERS.hsl.exec(color))) {
    return {h: match[1], s: match[2], l: match[3]};
  }
  if ((match = MATCHERS.hsla.exec(color))) {
    return {h: match[1], s: match[2], l: match[3], a: match[4]};
  }
  if ((match = MATCHERS.hsv.exec(color))) {
    return {h: match[1], s: match[2], v: match[3]};
  }
  if ((match = MATCHERS.hsva.exec(color))) {
    return {h: match[1], s: match[2], v: match[3], a: match[4]};
  }
  if ((match = MATCHERS.hex8.exec(color))) {
    return {r: parseInt(match[1], 16), g: parseInt(match[2], 16), b: parseInt(match[3], 16), a: parseInt(match[4], 16) / 255};
  }
  if ((match = MATCHERS.hex6.exec(color))) {
    return {r: parseInt(match[1], 16), g: parseInt(match[2], 16), b: parseInt(match[3], 16)};
  }
  if ((match = MATCHERS.hex3.exec(color))) {
    return {r: parseInt(match[1] + match[1], 16), g: parseInt(match[2] + match[2], 16), b: parseInt(match[3] + match[3], 16)};
  }
  return null;
}

function objectToColor(color: ColorObject | null): ParsedColor {
  let rgb = {r: 0, g: 0, b: 0};
  let ok = false;
  if (color && color.r !== undefined && color.g !== undefined && color.b !== undefined) {
    rgb = {r: bound01(color.r, 255) * 255, g: bound01(color.g, 255) * 255, b: bound01(color.b, 255) * 255};
    ok = true;
  } else if (color && color.h !== undefined && color.s !== undefined && color.v !== undefined) {
    rgb = hsvToRgb(color.h, convertToPercentage(color.s), convertToPercentage(color.v));
    ok = true;
  } else if (color && color.h !== undefined && color.s !== undefined && color.l !== undefined) {
    rgb = hslToRgb(color.h, convertToPercentage(color.s), convertToPercentage(color.l));
    ok = true;
  }
  const clamp = (n: number) => {
    const c = Math.min(255, Math.max(n, 0));
    // TinyColor rounds a channel under 1, so .5 is never read as half of the whole range.
    return c < 1 ? Math.round(c) : c;
  };
  return {r: clamp(rgb.r), g: clamp(rgb.g), b: clamp(rgb.b), a: boundAlpha(color?.a ?? 1), ok};
}

/** Reads typed text as TinyColor did; text it cannot read gives black with `ok` false. */
export function parseColor(text: string): ParsedColor {
  return objectToColor(text ? stringToObject(String(text)) : null);
}

/** The colour of the panel's state, as Spectrum built it: `tinycolor.fromRatio` with alpha at 3 decimals. */
export function hsvaToRgba(color: Hsva): Rgba {
  const {r, g, b, a} = objectToColor({
    h: convertToPercentage(color.h),
    s: convertToPercentage(color.s),
    v: convertToPercentage(color.v),
    a: Math.round(color.a * 1000) / 1000,
  });
  return {r, g, b, a};
}

/** The panel's state for a colour, as Spectrum's `set` derived it. */
export function rgbaToHsva(color: Rgba): Hsva {
  const r = bound01(color.r, 255);
  const g = bound01(color.g, 255);
  const b = bound01(color.b, 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (max !== min) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return {h: ((h * 360) % 360) / 360, s: max === 0 ? 0 : d / max, v: max, a: color.a};
}

const hex2 = (n: number) => Math.round(n).toString(16).padStart(2, '0');

/** `#rrggbb`, lowercase, never 3 digits; alpha is dropped. */
export function toHexString(color: Rgba): string {
  return `#${hex2(color.r)}${hex2(color.g)}${hex2(color.b)}`;
}

/** CSS `#rrggbbaa`. */
export function toHex8String(color: Rgba): string {
  return toHexString(color) + hex2(color.a * 255);
}

/** `rgb(r, g, b)`, or `rgba(r, g, b, a)` with a at 3 decimals. */
export function toRgbString(color: Rgba): string {
  const rgb = `${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)}`;
  return color.a === 1 ? `rgb(${rgb})` : `rgba(${rgb}, ${Math.round(color.a * 1000) / 1000})`;
}

/** Spectrum's test for a change: both colours present and the same rgb string. Two empty colours differ. */
export function sameColor(first: Rgba | null, second: Rgba | null): boolean {
  return !!first && !!second && toRgbString(first) === toRgbString(second);
}
